import _ from "lodash";
import { useEffect, useState, useCallback, useRef } from "react";
import { useHistory, useParams } from "react-router";

import { useFormik } from "formik";
import * as yup from "yup";
import scrollToErrors from "../../utils/scrollToErrors";

import TextField from "@material-ui/core/TextField";
import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";

import Button from "../../components/Button";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionReminderCard from "../../components/SessionReminderCard";
import Stepper from "../../components/Stepper";
import SubjectMetricTargetInput from "../../components/SubjectMetricTargetInput";
import { Framework, PDCAStages } from "../../config/Config";
import { useCampaign } from "../../context/campaign";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { specialCharacterRegex } from "../../utils/customValidation";

import GetSessionData from "../../services/getSessionData";

import { useTranslation } from "react-i18next";
import { SubjectableType } from "../../enums/SubjectableType";
import styles from "./SessionsCoachingForm.module.scss";

import AiPopover from "../../components/AiPopover";
import useNudgePopover from "../../hooks/useNudgePopover";
import usePiiGuard from "../../hooks/usePiiGuard";
import useCampaignCodes from "../../hooks/useCampaignCodes";
import useClientCompanyNames from "../../hooks/useClientCompanyNames";
import useSubFunctionalUnitNames from "../../hooks/useSubFunctionalUnitNames";
import PiiDetectionPanel from "../../components/PiiDetectionPanel";
import AiInsightDiv from "../../components/AiInsightDiv";
import AiBanner from "../../components/AiBanner";

dayjs.extend(utc);

// Mock data for skills
const mockSkillList = [
  { title: "Skill no.1" },
  { title: "Skill no.2" },
  { title: "Skill no.3" },
  { title: "Skill no.4" },
  { title: "Skill no.5" },
  { title: "Skill no.6" },
  { title: "Skill no.7" },
  { title: "Skill no.8" },
];

const GUARDED_FIELDS = [
  "kickstart_question",
  "awe_question",
  "focus_question",
  "foundation_question",
  "lazy_question",
  "strategic_question",
  "learning_question",
  "goal",
  "reality_optional",
  "opportunity",
  "way_forward",
];

export function SessionsCoachingForm() {
  const history = useHistory();
  const { id } = useParams();
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [subjectList, setSubjectList] = useState([]);
  const [sessionData, setSessionData] = useState({});
  const [growFormDetails, setGrowFormDetails] = useState({});
  const [sessionReviewDate, setSessionReviewDate] = useState("");
  const [sessionDuration, setSessionDuration] = useState(0);
  const [skillRequiredData, setSkillRequiredData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hideProjectTargetKpi, sethideProjectTargetKpi] = useState(false);
  const [hideCurrentMetrics, sethideCurrentMetricsi] = useState(false);
  const [hideTargetMetrics, sethhideTargetMetrics] = useState(false);

  const [fieldFeedbackStates, setFieldFeedbackStates] = useState({
    goal: null,
    opportunity: null,
    reality_optional: null,
    way_forward: null,
    kickstart_question: null,
    awe_question: null,
    focus_question: null,
    foundation_question: null,
    lazy_question: null,
    strategic_question: null,
    learning_question: null,
  });

  const prevFieldValues = useRef({
    goal: "",
    opportunity: "",
    reality_optional: "",
    way_forward: "",
    kickstart_question: "",
    awe_question: "",
    focus_question: "",
    foundation_question: "",
    lazy_question: "",
    strategic_question: "",
    learning_question: "",
  });

  const fieldAnchorRefs = useRef({});
  const getFieldAnchor = fieldName => fieldAnchorRefs.current[fieldName];

  const getMainSubjectId = () => {
    return subjectList?.[0]?.subject_id || null;
  };

  const {
    popoverAnchorEl,
    streamedText,
    nudgeType,
    streaming,
    error,
    isOpen,
    aiDisabled,
    manuallyDisabled,
    handleNudgeRequest,
    handleClosePopover,
    handleFeedback,
    handleRefresh,
    handleDisableAI,
    handleEnableAI,
  } = useNudgePopover(id, getMainSubjectId);

  // AI Coaching pilot region gate: useNudgePopover already folds the region
  // gate into aiDisabled above (blocking requests/popover) and into
  // AiInsightDiv's own visibility. setIsAiCoachingEnabled (not the read) is
  // still needed here to feed this session's region into that shared state.
  // See alt-ai-coaching-pilot.md.
  const { setIsAiCoachingEnabled } = useCampaign();

  const campaignCodes = useCampaignCodes();
  const clientCompanyNames = useClientCompanyNames();
  const subFunctionalUnitNames = useSubFunctionalUnitNames();
  const piiGuard = usePiiGuard(
    id,
    "coaching_session",
    sessionData.coachee_name,
    sessionData.coach_name,
    campaignCodes,
    clientCompanyNames,
    subFunctionalUnitNames
  );

  const piiScannedRef = useRef({});

  const intialForm = {
    session_id: id,
    goal: "",
    opportunity: "",
    way_forward: "",
    kickstart_question: "",
    awe_question: "",
    focus_question: "",
    foundation_question: "",
    lazy_question: "",
    strategic_question: "",
    learning_question: "",
    duration: {
      duration: sessionDuration,
      session_phase: PDCAStages.DO,
    },
    created_by: 0,
    grow_form_subject_details: [
      {
        subject_id: null,
        reality_target: null,
        skill_improve_id: null,
        skill_improve_text: null,
      },
    ],
    subjects: [],
    reality_optional: "",
  };

  if (!localStorage.sessionCoachingFormData) {
    localStorage.setItem("sessionCoachingFormData", JSON.stringify(intialForm));
  }

  const localData =
    localStorage.sessionCoachingFormData &&
    localStorage.sessionCoachingFormData !== undefined
      ? JSON.parse(localStorage.sessionCoachingFormData)
      : {};

  //create store for subject lists
  if (!localStorage.subjectListData) {
    const initialSubjectList = { subjectList: [] };
    localStorage.setItem("subjectListData", JSON.stringify(initialSubjectList));
  }
  const localSubjectListData =
    localStorage.subjectListData && localStorage.subjectListData !== undefined
      ? JSON.parse(localStorage.subjectListData)
      : {};

  const [formState, setFormState] = useState({
    session_id: localData?.id ?? id,
    goal: localData?.goal ?? "",
    opportunity: localData?.opportunity ?? "",
    way_forward: localData?.way_forward ?? "",
    learning_question: localData?.learning_question ?? "",
    strategic_question: localData?.strategic_question ?? "",
    kickstart_question: localData?.kickstart_question ?? "",
    awe_question: localData?.awe_question ?? "",
    focus_question: localData?.focus_question ?? "",
    foundation_question: localData?.foundation_question ?? "",
    lazy_question: localData?.lazy_question ?? "",
    created_by: localData?.created_by ?? getLocalValue("uid"),
    // if theres any localData.grow_form_subject_details, use it.
    // else, use localSubjectListData.subjectList array. else, default to empty array
    grow_form_subject_details: localData?.grow_form_subject_details?.length
      ? localData.grow_form_subject_details
      : (localSubjectListData?.subjectList?.map(subject => ({
          subject_id: subject.subject_id,
          reality_target: subject.reality_target,
          skill_improve_id: subject.skill_improve_id,
          skill_improve_text: subject.skill_improvement_level_1,
        })) ?? []),
    duration: {
      duration: localData?.duration?.duration ?? sessionDuration,
      session_phase: localData?.duration?.session_phase ?? PDCAStages.DO,
    },
    reality_optional: localData?.reality_optional,
  });

  // Known-value lists load async - earlier scans ran against empty arrays,
  // so force a re-scan of every guarded field once they're populated.
  useEffect(() => {
    piiScannedRef.current = {};
  }, [campaignCodes, clientCompanyNames]);

  // Scan values restored from a saved draft (via enableReinitialize), not
  // just ones the user typed and then blurred - handleFieldBlur alone never
  // sees a hydrated value since no blur event fires for it.
  useEffect(() => {
    GUARDED_FIELDS.forEach(fieldName => {
      const value = formState?.[fieldName];
      if (value?.trim() && piiScannedRef.current[fieldName] !== value) {
        piiScannedRef.current[fieldName] = value;
        piiGuard.checkField(fieldName, value);
      }
    });
  }, [formState, campaignCodes, clientCompanyNames, piiGuard]);

  const restFormValues = () => {
    localStorage.removeItem("sessionCoachingFormData");
    localStorage.removeItem("subjectListData");
  };

  //Update session value to form
  useEffect(() => {
    let newFormState = _.cloneDeep(formState);
    newFormState["duration"]["duration"] = sessionDuration;
    setFormState(newFormState);
    localStorage.setItem(
      `sessionCoachingFormData`,
      JSON.stringify(newFormState)
    );
  }, [sessionDuration]);

  //Update session value to form
  useEffect(() => {
    if (!_.isEmpty(sessionData)) {
      let data = {
        sessionTypeId: sessionData.session_type_id,
        sessionFramework: sessionData.framework,
        sessionMethod: sessionData.method,
      };
      setSkillRequiredData(data);
    }
  }, [sessionData]);

  // fetch sessionDetails and subjectList
  useEffect(() => {
    async function fetchSessionData() {
      setIsLoading(true);
      const sessionAPIData = await GetSessionData(id);
      if (sessionAPIData) {
        setSessionData(sessionAPIData.session);
        // AI Coaching pilot region gate: derived from this specific session's
        // campaign -> region, refreshed on every load of this page (bookmark,
        // refresh, or direct nav) so it doesn't depend on CampaignList having
        // run earlier in the browser session. See alt-ai-coaching-pilot.md.
        setIsAiCoachingEnabled(Boolean(sessionAPIData.ai_coaching_enabled));
      } else {
        addNotification(
          `Could not get session details from id #${id}. (Status code: ${sessionAPIData.status})`,
          "error"
        );
      }
      fetchGrowFormDetails(); // fetch growFormDetails afterwards
      setIsLoading(false);
    }
    // fetch growFormDetails
    async function fetchGrowFormDetails() {
      const response = await apiRequest.get(
        `/api/getGrowformDetails?session_id=${id}`
      );

      if (response.status === 200) {
        const data = response.data.data;

        if (data.subject && data.subject.length > 0) {
          let formSubjects = data.subject.map(obj => {
            //  formik.setFieldValue('')
            return {
              //  id: obj.subject_id,
              reality_target: obj.reality_target,
              skill_improve_id: obj.skill_improve_id,
              skill_improve_text: obj.skill_improve_text,
            };
          });
          formik.initialValues.subjects = formSubjects;
        }
        //setSubjectList(data.subject);
        setGrowFormDetails(data.grow_form_static_data);

        //store the subjects into local storage
        if (localStorage.subjectListData) {
          const tmp = JSON.parse(localStorage.subjectListData);

          tmp.subjectList = data.subject;
          localStorage.setItem("subjectListData", JSON.stringify(tmp));
          setSubjectList(data.subject);
          formik.setFieldValue("subjects", data.subject);
        } else {
          const tmp = { subjectList: [] };
          tmp.subjectList = data.subject;
          localStorage.setItem("subjectListData", JSON.stringify(tmp));
          setSubjectList(data.subject);
          formik.setFieldValue("subjects", data.subject);
        }

        // update formstate
        let newFormState = _.cloneDeep(formState);
        newFormState.goal = localData?.goal
          ? localData.goal
          : data.grow_form_static_data.goal;
        newFormState.opportunity = localData?.opportunity
          ? localData.opportunity
          : data.grow_form_static_data.opportunity;
        newFormState.way_forward = localData?.way_forward
          ? localData.way_forward
          : data.grow_form_static_data.way_forward;

        newFormState.kickstart_question = localData?.kickstart_question
          ? localData.kickstart_question
          : data.grow_form_static_data.kickstart_question;
        newFormState.awe_question = localData?.awe_question
          ? localData.awe_question
          : data.grow_form_static_data.awe_question;
        newFormState.focus_question = localData?.focus_question
          ? localData.focus_question
          : data.grow_form_static_data.focus_question;

        newFormState.foundation_question = localData?.foundation_question
          ? localData.foundation_question
          : data.grow_form_static_data.foundation_question;
        newFormState.lazy_question = localData?.lazy_question
          ? localData.lazy_question
          : data.grow_form_static_data.lazy_question;
        newFormState.strategic_question = localData?.strategic_question
          ? localData.strategic_question
          : data.grow_form_static_data.strategic_question;
        newFormState.learning_question = localData?.learning_question
          ? localData.learning_question
          : data.grow_form_static_data.learning_question;
        newFormState.reality_optional = localData?.reality_optional
          ? localData?.reality_optional
          : data.grow_form_static_data.reality_optional;
        formik.setFieldValue("goal", newFormState.goal);
        formik.setFieldValue("opportunity", newFormState.opportunity);
        formik.setFieldValue("way_forward", newFormState.way_forward);
        formik.setFieldValue(
          "kickstart_question",
          newFormState.kickstart_question
        );
        formik.setFieldValue("awe_question", newFormState.awe_question);
        formik.setFieldValue("focus_question", newFormState.focus_question);
        formik.setFieldValue(
          "foundation_question",
          newFormState.foundation_question
        );
        formik.setFieldValue("lazy_question", newFormState.lazy_question);
        formik.setFieldValue(
          "strategic_question",
          newFormState.strategic_question
        );
        formik.setFieldValue(
          "learning_question",
          newFormState.learning_question
        );
        formik.setFieldValue("reality_optional", newFormState.reality_optional);
        setFormState(newFormState);
      } else {
        addNotification(
          t("error.couldNotGetGrowFormDetailsFromSessionId") +
            ` #${id}. (Status code: ${response.status})`,
          "error"
        );
      }
    }

    fetchSessionData();
  }, []);

  useEffect(() => {
    if (sessionData && sessionData.session_type_id === 2) {
      sethideProjectTargetKpi(true);
      sethideCurrentMetricsi(true);
      sethhideTargetMetrics(true);
      // console.log(response.session_type_id,'response')
    } else {
      sethideProjectTargetKpi(false);
      sethideCurrentMetricsi(false);
      sethhideTargetMetrics(false);
    }
  }, [sessionData]);
  // fetch session review date
  useEffect(() => {
    apiRequest
      .get(`/api/getSessionReviewDate?session_id=${id}`)
      .then(res => {
        if (res && res.data.data) {
          let newDate = res.data.data.current_review_date[0].review_datetime;
          newDate = dayjs(new Date(newDate)).format("DD MMM YYYY");
          setSessionReviewDate(newDate);
        }
      })
      .finally(() => {});
  }, []);

  const updateFormSubjects = subjList => {
    if (subjList && subjList.length > 0) {
      let formSubjects = subjList.map((obj, index) => {
        let updatedSubject = {
          subject_id: obj.subject_id,
          reality_target: obj.reality_target,
        };

        // Check if session_type_id === 2
        if (sessionData.session_type_id !== 2) {
          // Add skill_improve_id if session_type_id !== 2
          updatedSubject.skill_improve_id = obj.skill_improve_id;
          // Update formik field value
          formik.setFieldValue(
            `subjects.${index}.skill_improve_id`,
            obj.skill_improve_id
          );
        }
        return updatedSubject;
      });
      handleFormChange("grow_form_subject_details", formSubjects);
    }
  };

  // update subjects list on formState upon changing subjectList
  useEffect(() => {
    updateFormSubjects(subjectList);
  }, [subjectList]);

  const handleFieldBlur = useCallback(
    (fieldName, label, value, e) => {
      if (value?.trim() && value !== prevFieldValues.current[fieldName]) {
        prevFieldValues.current[fieldName] = value;
        const hits = piiGuard.checkField(fieldName, value);
        if (hits.length > 0) return;
        const anchorEl = getFieldAnchor(fieldName);
        handleNudgeRequest(
          fieldName,
          label,
          value,
          anchorEl,
          [...subjectList],
          type =>
            setFieldFeedbackStates(prev => ({ ...prev, [fieldName]: type }))
        );
      }
    },
    [handleNudgeRequest, subjectList, piiGuard]
  );

  const handleAiInsightClick = useCallback(
    (fieldName, label, value, e) => {
      const hits = piiGuard.checkField(fieldName, value);
      if (hits.length > 0) return;
      const anchorEl = getFieldAnchor(fieldName);
      handleNudgeRequest(
        fieldName,
        label,
        value,
        anchorEl,
        [...subjectList],
        type => setFieldFeedbackStates(prev => ({ ...prev, [fieldName]: type }))
      );
    },
    [handleNudgeRequest, subjectList, piiGuard]
  );

  // helper method to handle form changes
  const handleFormChange = (key, value) => {
    let newFormState = _.cloneDeep(formState);
    newFormState[key] = value;
    setFormState(newFormState);
    localStorage.setItem(
      `sessionCoachingFormData`,
      JSON.stringify(newFormState)
    );
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  // upon clicking on confirm
  const handleConfirm = () => {
    let newFormState = _.cloneDeep(formState);

    if (subjectList && subjectList.length > 0) {
      // making sure subject list on formstate is updated
      newFormState.grow_form_subject_details = subjectList.map(obj => {
        return {
          subject_id: obj.subject_id,
          reality_target: obj.reality_target,
          skill_improve_id: obj.skill_improve_id,
          skill_improve_text: obj.skill_improve_text,
        };
      });
    }

    newFormState.created_by = sessionData.coach_id;
    newFormState.session_id = sessionData.session_id;

    setIsLoading(true);
    apiRequest
      .post(`/api/editGrowFormDetails`, {
        ...newFormState,
      })
      .then(response => {
        setIsLoading(false);
        restFormValues();
        localStorage.removeItem("sessionCoachingFormData");
        localStorage.removeItem("subjectListData");
        history.push(`/sessions/coaching/summary/${id}`);
      })
      .catch(error => {
        setIsLoading(false);
        addNotification(`${error}`, "error");
      });
  };

  const isSpecialSession = sessionData?.session_type_id === 3;
  const isPDFlow = sessionData?.framework === Framework.PD;

  //Validation

  const validationSchema = yup.object().shape({
    kickstart_question: yup
      .string("Please provide a kickstart question")
      .when("isSpecialSession", {
        is: true,
        then: yup
          .string()
          .required("Kickstart question is mandatory")
          .matches(specialCharacterRegex, "Special characters are not allowed"),
        otherwise: yup.string(), // not required if not special session
      }),
    awe_question: yup
      .string("Please provide an awe question")
      .when("isSpecialSession", {
        is: true,
        then: yup
          .string()
          .required("Awe question is mandatory")
          .matches(specialCharacterRegex, "Special characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),
    focus_question: yup
      .string("Please provide a focus question")
      .when("isSpecialSession", {
        is: true,
        then: yup
          .string()
          .required("Focus question is mandatory")
          .matches(specialCharacterRegex, "Special characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),
    foundation_question: yup
      .string("Please provide a foundation question")
      .when("isSpecialSession", {
        is: true,
        then: yup
          .string()
          .required("Foundation question is mandatory")
          .matches(specialCharacterRegex, "Special characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),
    lazy_question: yup
      .string("Please provide a lazy question")
      .when("isSpecialSession", {
        is: true,
        then: yup
          .string()
          .required("Lazy question is mandatory")
          .matches(specialCharacterRegex, "Special characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),
    strategic_question: yup
      .string("Please provide a strategic question")
      .when("isSpecialSession", {
        is: true,
        then: yup
          .string()
          .required("Strategic question is mandatory")
          .matches(specialCharacterRegex, "Special characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),
    learning_question: yup
      .string("Please provide a learning question")
      .when("isSpecialSession", {
        is: true,
        then: yup
          .string()
          .required("Learning question is mandatory")
          .matches(specialCharacterRegex, "Special characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),

    goal: yup
      .string("Please provide a learning question")
      .when("isSpecialSession", {
        is: false,
        then: yup
          .string("Please add Goals to the session")
          .required("Goal field is mandatory")
          .matches(specialCharacterRegex, "Special Characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),

    opportunity: yup
      .string("Please add opportunity to the session")
      .when("isSpecialSession", {
        is: false,
        then: yup
          .string("Please add opportunity to the session")
          .required("Opportunity is mandatory")
          .matches(specialCharacterRegex, "Special Characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),

    way_forward: yup
      .string("Way Forward is mandatory")
      .when("isSpecialSession", {
        is: false,
        then: yup
          .string("Way Forward is mandatory")
          .required("Way Forward is mandatory")
          .matches(specialCharacterRegex, "Special Characters are not allowed"),
        otherwise: yup.string(), // not required if special session
      }),
    ...(sessionData.session_type_id !== 2 && {
      subjects: yup.array().of(
        yup.object().shape({
          // Always validate reality_target
          reality_target: yup
            .string("Please enter Reality target")
            .required("Please enter Target"),

          skill_improve_id: yup
            .number()
            .nullable()
            .when(["subjectable_type"], {
              is: subjectable_type =>
                !isSpecialSession &&
                subjectable_type === SubjectableType.SUBJECT_RESULT_METRICS,
              then: schema =>
                schema
                  .typeError(t("growForm.pleaseSelectSkillsToImprove"))
                  .required(t("growForm.pleaseSelectSkillsToImprove")),
              otherwise: schema => schema.notRequired().nullable(),
            }),

          skill_improve_text: yup
            .string()
            .nullable()
            .when(["subjectable_type"], {
              is: subjectable_type =>
                !isSpecialSession &&
                subjectable_type === SubjectableType.FLASH_REWARD_USER_KPIS,
              then: schema =>
                schema
                  .typeError(t("subjectManagement.enterSkillToImprove"))
                  .required(t("subjectManagement.enterSkillToImprove")),
              otherwise: schema => schema.notRequired().nullable(),
            }),
        })
      ),
    }),
    reality_optional: yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      goal: "",
      opportunity: "",
      way_forward: "",
      kickstart_question: "",
      awe_question: "",
      focus_question: "",
      foundation_question: "",
      lazy_question: "",
      strategic_question: "",
      learning_question: "",
      subjects: subjectList || [],
      isPDFlow: isPDFlow,
      isSpecialSession: isSpecialSession,
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    onSubmit: values => {
      handleConfirm();
    },
  });

  return (
    <DashboardLayout
      isSessionScoped
      appBarContent={
        <>
          {sessionData && (
            <Stepper
              isPDFlow={sessionData["framework"] === Framework.PD}
              phase={t("header.stepper.do")}
              phaseLabel="Do"
              sessionId={id}
              clockRun={true}
              getTotalTimeSpent={getTotalTime}
            />
          )}
        </>
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={sessionData["coach_id"] === parseInt(getLocalValue("uid"))}
          accessible={true}
        />
      }
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <SectionTitle>{t("growForm.startCoaching")}</SectionTitle>
            {manuallyDisabled && (
              <AiBanner
                aiDisabled
                onEnable={() => {
                  handleEnableAI();
                  addNotification(
                    t("aiPopover.enabledNotification"),
                    "success"
                  );
                }}
              />
            )}
            {sessionData && sessionData["framework"] !== Framework.PD && (
              <SessionReminderCard
                plain
                header={t("growForm.yourReviewSessionIsOn")}
                date={sessionReviewDate}
              />
            )}
            <div className={styles.detailsContainer}>
              {isSpecialSession ? (
                <>
                  <div className={styles.sectionlable}>
                    {t("growForm.projectTargetKpi")}
                  </div>
                  <SubjectMetricTargetInput
                    list={
                      subjectList && subjectList.length > 0 ? subjectList : []
                    }
                    setList={setSubjectList}
                    metricId={"subject_result_metrics_id"}
                    skillsRequired={skillRequiredData}
                    skillList={mockSkillList}
                    formValidation={formik}
                    hideProjectTargetKpi={hideProjectTargetKpi}
                    hideCurrentMetrics={hideCurrentMetrics}
                    hideTargetMetrics={hideTargetMetrics}
                    sessionId={id}
                    aiDisabled={aiDisabled}
                    handleDisableAI={handleDisableAI}
                    handleNudgeRequest={handleNudgeRequest}
                    popoverAnchorEl={popoverAnchorEl}
                    isOpen={isOpen}
                    streamedText={streamedText}
                    nudgeType={nudgeType}
                    streaming={streaming}
                    error={error}
                    handleClosePopover={handleClosePopover}
                    handleFeedback={handleFeedback}
                    handleRefresh={handleRefresh}
                  />
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.kickstartQuestion")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.subkickstartquestion")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div
                        className={`${styles.content} ${styles.fieldWithInsight}`}
                        ref={el => {
                          fieldAnchorRefs.current.kickstart_question = el;
                        }}
                      >
                        <TextField
                          className={styles.textarea}
                          multiline
                          rows={4}
                          name="kickstart_question"
                          inputProps={{ style: { resize: "both" } }}
                          value={formik.values.kickstart_question}
                          placeholder={t("growForm.comment")}
                          variant="outlined"
                          onChange={e => {
                            handleFormChange(
                              "kickstart_question",
                              e.target.value
                            );
                            formik.handleChange(e);
                          }}
                          onFocus={() =>
                            (prevFieldValues.current.kickstart_question =
                              formik.values.kickstart_question)
                          }
                          onBlur={e =>
                            handleFieldBlur(
                              "kickstart_question",
                              t("growForm.subkickstartquestion"),
                              formik.values.kickstart_question,
                              e
                            )
                          }
                          error={
                            (formik.touched.kickstart_question &&
                              Boolean(formik.errors.kickstart_question)) ||
                            piiGuard.isBlocked("kickstart_question")
                          }
                          helperText={
                            formik.touched.kickstart_question &&
                            formik.errors.kickstart_question
                          }
                        />
                      </div>
                      {piiGuard.isBlocked("kickstart_question") && (
                        <PiiDetectionPanel
                          hits={piiGuard.getHits("kickstart_question")}
                          onOverride={(hit, reason) =>
                            piiGuard.overrideHit(
                              "kickstart_question",
                              hit,
                              reason
                            )
                          }
                        />
                      )}
                      <AiInsightDiv
                        state={fieldFeedbackStates.kickstart_question}
                        onClick={e =>
                          handleAiInsightClick(
                            "kickstart_question",
                            t("growForm.subkickstartquestion"),
                            formik.values.kickstart_question,
                            e
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.theAweQuestion")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.theAweQuestionans")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.awe_question = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="awe_question"
                            value={formik.values.awe_question}
                            placeholder={t("growForm.comment")}
                            variant="outlined"
                            onChange={e => {
                              handleFormChange("awe_question", e.target.value);
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.awe_question =
                                formik.values.awe_question)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "awe_question",
                                t("growForm.theAweQuestionans"),
                                formik.values.awe_question,
                                e
                              )
                            }
                            error={
                              (formik.touched.awe_question &&
                                Boolean(formik.errors.awe_question)) ||
                              piiGuard.isBlocked("awe_question")
                            }
                            helperText={
                              formik.touched.awe_question &&
                              formik.errors.awe_question
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("awe_question") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("awe_question")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit("awe_question", hit, reason)
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.awe_question}
                          onClick={e =>
                            handleAiInsightClick(
                              "awe_question",
                              t("growForm.theAweQuestionans"),
                              formik.values.awe_question,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.thefocusQuestion")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.thefocusQuestionans")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.focus_question = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="focus_question"
                            value={formik.values.focus_question}
                            placeholder={t("growForm.comment")}
                            variant="outlined"
                            onChange={e => {
                              handleFormChange(
                                "focus_question",
                                e.target.value
                              );
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.focus_question =
                                formik.values.focus_question)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "focus_question",
                                t("growForm.thefocusQuestionans"),
                                formik.values.focus_question,
                                e
                              )
                            }
                            error={
                              (formik.touched.focus_question &&
                                Boolean(formik.errors.focus_question)) ||
                              piiGuard.isBlocked("focus_question")
                            }
                            helperText={
                              formik.touched.focus_question &&
                              formik.errors.focus_question
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("focus_question") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("focus_question")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit(
                                "focus_question",
                                hit,
                                reason
                              )
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.focus_question}
                          onClick={e =>
                            handleAiInsightClick(
                              "focus_question",
                              t("growForm.thefocusQuestionans"),
                              formik.values.focus_question,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.thefoudationQuestion")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.thefoudationQuestionans")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.foundation_question = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="foundation_question"
                            value={formik.values.foundation_question}
                            placeholder={t("growForm.comment")}
                            variant="outlined"
                            onChange={e => {
                              handleFormChange(
                                "foundation_question",
                                e.target.value
                              );
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.foundation_question =
                                formik.values.foundation_question)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "foundation_question",
                                t("growForm.thefoudationQuestionans"),
                                formik.values.foundation_question,
                                e
                              )
                            }
                            error={
                              (formik.touched.foundation_question &&
                                Boolean(formik.errors.foundation_question)) ||
                              piiGuard.isBlocked("foundation_question")
                            }
                            helperText={
                              formik.touched.foundation_question &&
                              formik.errors.foundation_question
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("foundation_question") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("foundation_question")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit(
                                "foundation_question",
                                hit,
                                reason
                              )
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.foundation_question}
                          onClick={e =>
                            handleAiInsightClick(
                              "foundation_question",
                              t("growForm.thefoudationQuestionans"),
                              formik.values.foundation_question,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.thelazyQuestion")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.thelazyQuestionans")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.lazy_question = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="lazy_question"
                            value={formik.values.lazy_question}
                            placeholder={t("growForm.comment")}
                            variant="outlined"
                            onChange={e => {
                              handleFormChange("lazy_question", e.target.value);
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.lazy_question =
                                formik.values.lazy_question)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "lazy_question",
                                t("growForm.thelazyQuestionans"),
                                formik.values.lazy_question,
                                e
                              )
                            }
                            error={
                              (formik.touched.lazy_question &&
                                Boolean(formik.errors.lazy_question)) ||
                              piiGuard.isBlocked("lazy_question")
                            }
                            helperText={
                              formik.touched.lazy_question &&
                              formik.errors.lazy_question
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("lazy_question") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("lazy_question")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit("lazy_question", hit, reason)
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.lazy_question}
                          onClick={e =>
                            handleAiInsightClick(
                              "lazy_question",
                              t("growForm.thelazyQuestionans"),
                              formik.values.lazy_question,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.thestrategicQuestion")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.thestrategicQuestionans")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.strategic_question = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="strategic_question"
                            value={formik.values.strategic_question}
                            placeholder={t("growForm.comment")}
                            variant="outlined"
                            onChange={e => {
                              handleFormChange(
                                "strategic_question",
                                e.target.value
                              );
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.strategic_question =
                                formik.values.strategic_question)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "strategic_question",
                                t("growForm.thestrategicQuestionans"),
                                formik.values.strategic_question,
                                e
                              )
                            }
                            error={
                              (formik.touched.strategic_question &&
                                Boolean(formik.errors.strategic_question)) ||
                              piiGuard.isBlocked("strategic_question")
                            }
                            helperText={
                              formik.touched.strategic_question &&
                              formik.errors.strategic_question
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("strategic_question") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("strategic_question")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit(
                                "strategic_question",
                                hit,
                                reason
                              )
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.strategic_question}
                          onClick={e =>
                            handleAiInsightClick(
                              "strategic_question",
                              t("growForm.thestrategicQuestionans"),
                              formik.values.strategic_question,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.thelearningQuestion")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.thelearningQuestionans")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.learning_question = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="learning_question"
                            value={formik.values.learning_question}
                            placeholder={t("growForm.comment")}
                            variant="outlined"
                            onChange={e => {
                              handleFormChange(
                                "learning_question",
                                e.target.value
                              );
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.learning_question =
                                formik.values.learning_question)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "learning_question",
                                t("growForm.thelearningQuestionans"),
                                formik.values.learning_question,
                                e
                              )
                            }
                            error={
                              (formik.touched.learning_question &&
                                Boolean(formik.errors.learning_question)) ||
                              piiGuard.isBlocked("learning_question")
                            }
                            helperText={
                              formik.touched.learning_question &&
                              formik.errors.learning_question
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("learning_question") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("learning_question")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit(
                                "learning_question",
                                hit,
                                reason
                              )
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.learning_question}
                          onClick={e =>
                            handleAiInsightClick(
                              "learning_question",
                              t("growForm.thelearningQuestionans"),
                              formik.values.learning_question,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <AiPopover
                    open={isOpen && !aiDisabled}
                    anchorEl={popoverAnchorEl}
                    onClose={handleClosePopover}
                    nudgeText={streamedText}
                    nudgeType={nudgeType}
                    streaming={streaming}
                    error={error}
                    onFeedback={handleFeedback}
                    onRefresh={handleRefresh}
                    onCloseError={handleClosePopover}
                    onDisableAI={handleDisableAI}
                  />
                </>
              ) : (
                <>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.goals")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>
                          {t("growForm.knowingYourCoachingSubjectsAndCreate")}
                        </p>
                        <p>{t("growForm.SMARTGolasIsAnEstablished")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div
                        className={`${styles.content} ${styles.fieldWithInsight}`}
                        ref={el => {
                          fieldAnchorRefs.current.goal = el;
                        }}
                      >
                        <TextField
                          className={styles.textarea}
                          multiline
                          rows={4}
                          inputProps={{ style: { resize: "both" } }}
                          variant="outlined"
                          name="goal"
                          value={formik.values.goal ?? ""}
                          onChange={e => {
                            handleFormChange("goal", e.target.value);
                            formik.handleChange(e);
                          }}
                          onFocus={() =>
                            (prevFieldValues.current.goal = formik.values.goal)
                          }
                          onBlur={e =>
                            handleFieldBlur(
                              "goal",
                              t(
                                "growForm.knowingYourCoachingSubjectsAndCreate"
                              ),
                              formik.values.goal,
                              e
                            )
                          }
                          error={
                            (formik.touched.goal &&
                              Boolean(formik.errors.goal)) ||
                            piiGuard.isBlocked("goal")
                          }
                          helperText={formik.touched.goal && formik.errors.goal}
                        />
                      </div>
                      {piiGuard.isBlocked("goal") && (
                        <PiiDetectionPanel
                          hits={piiGuard.getHits("goal")}
                          onOverride={(hit, reason) =>
                            piiGuard.overrideHit("goal", hit, reason)
                          }
                        />
                      )}
                      <AiInsightDiv
                        state={fieldFeedbackStates.goal}
                        onClick={e =>
                          handleAiInsightClick(
                            "goal",
                            t("growForm.knowingYourCoachingSubjectsAndCreate"),
                            formik.values.goal,
                            e
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.reality")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.knowingYourCurrentPerformance")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <SubjectMetricTargetInput
                        list={
                          subjectList && subjectList.length > 0
                            ? subjectList
                            : []
                        }
                        setList={setSubjectList}
                        metricId={"subject_result_metrics_id"}
                        skillsRequired={skillRequiredData}
                        skillList={mockSkillList}
                        formValidation={formik}
                        hideProjectTargetKpi={hideProjectTargetKpi}
                        hideCurrentMetrics={hideCurrentMetrics}
                        hideTargetMetrics={hideTargetMetrics}
                        sessionId={id}
                        aiDisabled={aiDisabled}
                        handleDisableAI={handleDisableAI}
                        handleNudgeRequest={handleNudgeRequest}
                        popoverAnchorEl={popoverAnchorEl}
                        isOpen={isOpen}
                        streamedText={streamedText}
                        nudgeType={nudgeType}
                        streaming={streaming}
                        error={error}
                        handleClosePopover={handleClosePopover}
                        handleFeedback={handleFeedback}
                        handleRefresh={handleRefresh}
                      />
                      <hr style={{ backgroundColor: "#BDBDBD" }} />
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.reality_optional = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={5}
                            inputProps={{ style: { resize: "both" } }}
                            name="reality_optional"
                            variant="outlined"
                            value={formik.values.reality_optional ?? ""}
                            onChange={e => {
                              handleFormChange(
                                "reality_optional",
                                e.target.value
                              );
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.reality_optional =
                                formik.values.reality_optional)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "reality_optional",
                                t("growForm.reality"),
                                formik.values.reality_optional,
                                e
                              )
                            }
                            error={
                              (formik.touched.reality_optional &&
                                Boolean(formik.errors.reality_optional)) ||
                              piiGuard.isBlocked("reality_optional")
                            }
                            helperText={
                              formik.touched.reality_optional &&
                              formik.errors.reality_optional
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("reality_optional") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("reality_optional")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit(
                                "reality_optional",
                                hit,
                                reason
                              )
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.reality_optional}
                          onClick={e =>
                            handleAiInsightClick(
                              "reality_optional",
                              t("growForm.reality"),
                              formik.values.reality_optional,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.options")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>
                          {t("growForm.brainstormForMultipleOptionsToImprove")}
                        </p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.opportunity = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="opportunity"
                            variant="outlined"
                            value={formik.values.opportunity ?? ""}
                            onChange={e => {
                              handleFormChange("opportunity", e.target.value);
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.opportunity =
                                formik.values.opportunity)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "opportunity",
                                t(
                                  "growForm.brainstormForMultipleOptionsToImprove"
                                ),
                                formik.values.opportunity,
                                e
                              )
                            }
                            error={
                              (formik.touched.opportunity &&
                                Boolean(formik.errors.opportunity)) ||
                              piiGuard.isBlocked("opportunity")
                            }
                            helperText={
                              formik.touched.opportunity &&
                              formik.errors.opportunity
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("opportunity") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("opportunity")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit("opportunity", hit, reason)
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.opportunity}
                          onClick={e =>
                            handleAiInsightClick(
                              "opportunity",
                              t(
                                "growForm.brainstormForMultipleOptionsToImprove"
                              ),
                              formik.values.opportunity,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {" "}
                        {t("growForm.wayForward")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.selectTheMostFeasibleOptions")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.way_forward = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="way_forward"
                            variant="outlined"
                            value={formik.values.way_forward ?? ""}
                            onChange={e => {
                              handleFormChange("way_forward", e.target.value);
                              formik.handleChange(e);
                            }}
                            onFocus={() =>
                              (prevFieldValues.current.way_forward =
                                formik.values.way_forward)
                            }
                            onBlur={e =>
                              handleFieldBlur(
                                "way_forward",
                                t("growForm.selectTheMostFeasibleOptions"),
                                formik.values.way_forward,
                                e
                              )
                            }
                            error={
                              (formik.touched.way_forward &&
                                Boolean(formik.errors.way_forward)) ||
                              piiGuard.isBlocked("way_forward")
                            }
                            helperText={
                              formik.touched.way_forward &&
                              formik.errors.way_forward
                            }
                          />
                        </div>
                        {piiGuard.isBlocked("way_forward") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("way_forward")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit("way_forward", hit, reason)
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.way_forward}
                          onClick={e =>
                            handleAiInsightClick(
                              "way_forward",
                              t("growForm.selectTheMostFeasibleOptions"),
                              formik.values.way_forward,
                              e
                            )
                          }
                        />
                        <AiPopover
                          open={isOpen && !aiDisabled}
                          anchorEl={popoverAnchorEl}
                          onClose={handleClosePopover}
                          nudgeText={streamedText}
                          nudgeType={nudgeType}
                          streaming={streaming}
                          error={error}
                          onFeedback={handleFeedback}
                          onRefresh={handleRefresh}
                          onCloseError={handleClosePopover}
                          onDisableAI={handleDisableAI}
                        />
                        <hr style={{ backgroundColor: "#BDBDBD" }} />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.detailsContainer}>
              <div className={styles.confirmButtonWrapper}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled={piiGuard.hasAnyBlocking}
                  // onClick={handleConfirm}
                  onClick={() => {
                    formik.handleSubmit();
                    scrollToErrors(formik);
                  }}
                  isloading={isLoading ? 1 : 0}
                >
                  {t("growForm.proceedToSummary")}
                </Button>
              </div>
            </div>
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
